import React, { useState, useEffect } from "react";
import { Button, Paper, Typography, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { useForm } from "react-hook-form";
import formatDate from "../utils/formatDate";

const useStyles = makeStyles(() => ({
  // formContainer: {
  //   maxWidth: "600px",
  //   margin: "auto",
  //   padding: "20px",
  //   marginTop: "40px",
  //   borderRadius: "8px",
  //   boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  // },
  inputField: {
    marginBottom: "16px",
  },
  // buttonGroup: {
  //   display: "flex",
  //   justifyContent: "space-between",
  //   marginTop: "20px",
  // },
  errorText: {
    color: "red",
    fontSize: "0.9rem",
    marginTop: "1px",
    marginBottom: "10px",
  },
}));

const UpdateUser = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { userId } = useParams();
  const [loader, setLoader] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const { register, handleSubmit, setValue, formState: { errors } } = useForm();

  useEffect(() => {
    const fetchUserData = async () => {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      try {
        // Retrieve the token from localStorage
        const token = localStorage.getItem("userToken");
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch user details by ID
        const response = await axios.post(`${API_BASE_URL}/user/get-user-by-id`, { userId }, { headers });

        if (response?.data?.status === "SUCCESS") {
          const userData = response.data.user;
          // Set default values for the form
          for (const [key, value] of Object.entries(userData)) {
            if (key === "dob" && value) {
              // Format the dob to YYYY-MM-DD before setting it
              setValue(key, new Date(value).toISOString().split("T")[0]);
            } else {
              setValue(key, value);
            }
          }
        } else {
          Swal.fire({
            title: "Error",
            text: response?.data?.message || "Failed to fetch user details.",
            icon: "error",
            confirmButtonText: "Close",
          });
        }
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: "An error occurred while fetching user details.",
          icon: "error",
          confirmButtonText: "Close",
        });
        console.error("Error fetching user data:", error);
      } finally {
        setLoadingData(false);
      }
    };

    fetchUserData();
  }, [userId, setValue]);

  const onSubmit = async (data) => {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    try {
      setLoader(true);
      const payload = { ...data, userId };

      // Retrieve the token from localStorage
      const token = localStorage.getItem("userToken");
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.post(`${API_BASE_URL}/user/update-user`, payload, { headers });

      if (response?.data?.status === "SUCCESS") {
        Swal.fire({
          title: "User Updated",
          text: "The user has been successfully updated.",
          icon: "success",
          confirmButtonText: "Okay",
        });
        navigate("/user-list");
      } else {
        Swal.fire({
          title: "Updation Failed",
          text: response?.data?.message || "Failed to update the user. Please try again.",
          icon: "error",
          confirmButtonText: "Try Again",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "An error occurred while updating the user.",
        icon: "error",
        confirmButtonText: "Close",
      });
      console.error("Error during user updation:", error);
    } finally {
      setLoader(false);
    }
  };

  if (loadingData) {
    return <Typography>Loading user data...</Typography>;
  }

  return (
    <Paper style={{
      maxWidth: "600px",
      margin: "auto",
      padding: "20px",
      marginTop: "40px",
      borderRadius: "8px",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    }}>
      <Typography variant="h5" gutterBottom>
        Update User
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <label>Name</label>
            <input
              type="text"
              {...register("name", {
                required: "Name is required",
                minLength: { value: 3, message: "Name must be at least 3 characters long" },
              })}
              className="form-control"
            />
            {errors?.name && <p className={classes.errorText}>{errors?.name?.message}</p>}
          </Grid>

          <Grid item xs={12}>
            <label>Email</label>
            <input
              type="email"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: "Enter a valid email address",
                },
              })}
              className="form-control"
            />
            {errors?.email && <p className={classes.errorText}>{errors?.email?.message}</p>}
          </Grid>

          <Grid item xs={12}>
            <label>Mobile</label>
            <input
              type="text"
              {...register("mobile", {
                required: "Mobile number is required",
                pattern: {
                  value: /^[0-9]{10}$/,
                  message: "Mobile number must be 10 digits",
                },
              })}
              className="form-control"
            />
            {errors?.mobile && <p className={classes.errorText}>{errors?.mobile?.message}</p>}
          </Grid>

          <Grid item xs={12}>
            <label>Date of Birth</label>
            <input
              type="date"
              {...register("dob", { required: "Date of Birth is required" })}
              className="form-control"
            />
            {errors?.dob && <p className={classes.errorText}>{errors?.dob?.message}</p>}
          </Grid>

          <Grid item xs={12}>
            <label>Password</label>
            <input
              type="password"
              {...register("password", {
                required: "Password is required",
                minLength: { value: 8, message: "Password must be at least 8 characters" },
                maxLength: { value: 15, message: "Password cannot exceed 15 characters" },
              })}
              className="form-control"
            />
            {errors?.password && <p className={classes.errorText}>{errors?.password?.message}</p>}
          </Grid>
        </Grid>
        <div className="buttonGroup_add">
          <Button
            variant="outlined"
            onClick={() => navigate("/user-list")}
            disabled={loader}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loader}
          >
            {loader ? "Updating..." : "Update User"}
          </Button>
        </div>
      </form>
    </Paper>
  );
};

export default UpdateUser;
